import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { getPublicStats } from "../lib/publicStats";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { ChartBar, Eye, Phone, WhatsappLogo, Envelope, Sparkle, ChartLine, Lightning, ChatCircleText, CurrencyEur, Crown, FilePdf, QrCode, ShareNetwork } from "@phosphor-icons/react";
import { toast } from "sonner";
import { PhotoGallery, LogoUploader } from "../components/ImageUpload";
import HoursEditor from "../components/HoursEditor";
import BadgePanel from "../components/BadgePanel";
import OnboardingWizard from "../components/OnboardingWizard";
import WelcomeLottie from "../components/WelcomeLottie";
import JustJoinedPopup from "../components/JustJoinedPopup";
import AIToolsPanel from "../components/AIToolsPanel";
import ReferralPanel from "../components/ReferralPanel";
import ReviewsManager from "../components/ReviewsManager";
import ReviewsWidgetPanel from "../components/ReviewsWidgetPanel";
import SubscriptionPanel from "../components/SubscriptionPanel";
import ImpactPanel from "../components/ImpactPanel";
import TrialWelcomeBanner from "../components/TrialWelcomeBanner";
import InsightsPanel from "../components/InsightsPanel";
import RgpdPanel from "../components/RgpdPanel";
import PostClaimOnboarding from "../components/dashboard/PostClaimOnboarding";

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [params] = useSearchParams();
  const [bizs, setBizs] = useState([]);
  const [active, setActive] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [edit, setEdit] = useState(null);
  const [flyerQuota, setFlyerQuota] = useState(null);
  const [flyerBusy, setFlyerBusy] = useState(false);
  const [reportBusy, setReportBusy] = useState(false);
  const [statsCount, setStatsCount] = useState(null);

  useEffect(() => {
    getPublicStats().then((r) => setStatsCount(r.data?.businesses)).catch(() => {});
  }, []);

  const reload = () => {
    api.get("/dashboard/businesses").then((r) => {
      setBizs(r.data);
      const slugFromUrl = params.get("slug");
      const next = (slugFromUrl && r.data.find((b) => b.slug === slugFromUrl)) || r.data[0] || null;
      setActive(next);
    });
  };

  useEffect(() => { if (user) reload(); /* eslint-disable-next-line */ }, [user]);

  // Auto-reconcile PayPal subscription when returning from PayPal approval
  useEffect(() => {
    if (!user) return;
    if (params.get("subscribed") === "1") {
      api.post("/paypal/subscriptions/reconcile")
        .then((r) => {
          if (r.data?.activated > 0) {
            toast.success("Abonnement PayPal activé ✓");
            reload();
          } else if (r.data?.checked > 0) {
            toast.info("Abonnement en attente d'approbation PayPal. Veuillez patienter quelques secondes et recharger.");
          }
        })
        .catch(() => {});
    }
  /* eslint-disable-next-line */
  }, [user, params]);

  useEffect(() => {
    if (!active) return;
    api.get(`/dashboard/businesses/${active.slug}/analytics`).then((r) => setAnalytics(r.data));
    api.get(`/dashboard/flyer/${active.slug}/quota`).then((r) => setFlyerQuota(r.data)).catch(() => setFlyerQuota(null));
    setEdit({
      name: active.name,
      description: active.description?.fr || "",
      phone: active.phone || "",
      whatsapp: active.whatsapp || "",
      email: active.email || "",
      website: active.website || "",
      address: active.address || "",
      city: active.city || "",
      postal_code: active.postal_code || "",
      category: active.category || "",
      hours: active.hours || {},
    });
  }, [active]);

  const tier = active?.premium?.tier || "free";
  const isPremium = tier !== "free";

  // Onboarding wizard: show once when the partner reaches Premium (trial or paid)
  // and hasn't completed the setup yet. Dismissible. Auto-hides forever after complete.
  const needsOnboarding = isPremium && active && !active.onboarding_completed_at;
  const [showOnboarding, setShowOnboarding] = useState(false);
  useEffect(() => {
    if (needsOnboarding) {
      // Small delay so the Dashboard feels "loaded" before the modal appears
      const t = setTimeout(() => setShowOnboarding(true), 400);
      return () => clearTimeout(t);
    }
    setShowOnboarding(false);
  }, [needsOnboarding, active?.slug]);

  // Welcome Lottie guide — shown once per user on first dashboard visit.
  // Dismissible + replayable via the "Relancer le guide" button or the
  // custom event `mc68:welcome-replay`.
  const welcomeKey = user?.id ? `mc68_welcome_seen_${user.id}` : null;
  const [showWelcome, setShowWelcome] = useState(false);
  useEffect(() => {
    if (!welcomeKey) return;
    const seen = typeof window !== "undefined" && localStorage.getItem(welcomeKey) === "1";
    if (!seen && !showOnboarding) {
      const t = setTimeout(() => setShowWelcome(true), 600);
      return () => clearTimeout(t);
    }
  }, [welcomeKey, showOnboarding]);
  useEffect(() => {
    const handler = () => setShowWelcome(true);
    window.addEventListener("mc68:welcome-replay", handler);
    return () => window.removeEventListener("mc68:welcome-replay", handler);
  }, []);
  const closeWelcome = () => {
    if (welcomeKey) localStorage.setItem(welcomeKey, "1");
    setShowWelcome(false);
  };

  if (!user) return <div className="p-20 text-center text-muted-foreground" data-testid="dashboard-not-logged">Connectez-vous d'abord.</div>;

  const saveProfile = async () => {
    try {
      await api.put(`/dashboard/businesses/${active.slug}`, {
        name: edit.name,
        phone: edit.phone,
        whatsapp: edit.whatsapp,
        email: edit.email,
        website: edit.website,
        address: edit.address,
        city: edit.city,
        postal_code: edit.postal_code,
        category: edit.category,
        hours: edit.hours,
        description: { fr: edit.description },
      });
      toast.success("Fiche mise à jour");
      reload();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const downloadFlyer = async () => {
    if (!active) return;
    setFlyerBusy(true);
    try {
      const r = await api.get(`/dashboard/flyer/${active.slug}`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `flyer-${active.slug}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Flyer PDF téléchargé");
      // refresh quota
      const q = await api.get(`/dashboard/flyer/${active.slug}/quota`);
      setFlyerQuota(q.data);
    } catch (e) {
      const msg = e?.response?.data?.detail;
      if (e?.response?.status === 402) toast.error("Réservé aux partenaires Premium");
      else if (e?.response?.status === 429) toast.error(msg || "Quota mensuel atteint");
      else toast.error(msg || "Erreur lors de la génération");
    }
    setFlyerBusy(false);
  };

  const downloadReport = async () => {
    if (!active) return;
    setReportBusy(true);
    try {
      const r = await api.get(`/dashboard/report/${active.slug}`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rapport-mensuel-${active.slug}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Rapport mensuel téléchargé");
    } catch (e) {
      const msg = e?.response?.data?.detail;
      if (e?.response?.status === 402) toast.error("Réservé aux partenaires Premium");
      else toast.error(msg || "Erreur lors de la génération");
    }
    setReportBusy(false);
  };

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-10 py-12" data-testid="dashboard-page">
      {showOnboarding && active && (
        <OnboardingWizard
          biz={active}
          onClose={() => setShowOnboarding(false)}
          onBusinessUpdated={reload}
        />
      )}
      <WelcomeLottie open={showWelcome} onClose={closeWelcome} />
      <JustJoinedPopup />
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="label-uppercase mb-2">Bienvenue {user.email}</div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-medium">Espace partenaire</h1>
        </div>
        <button
          type="button"
          onClick={() => setShowWelcome(true)}
          data-testid="dashboard-replay-welcome-btn"
          className="shrink-0 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition border border-border rounded-full px-3 py-1.5 hover:border-primary/40"
        >
          <Sparkle size={14} />
          Relancer le guide
        </button>
      </div>

      {bizs.length === 0 ? (
        <div data-testid="dashboard-empty">
          <div className="bg-card border border-border rounded-sm p-8 mb-6">
            <div className="max-w-xl">
              <div className="label-uppercase mb-2">Premier pas</div>
              <h2 className="font-display text-2xl sm:text-3xl font-medium mb-3">Trouvez votre entreprise</h2>
              <p className="text-muted-foreground mb-6" data-testid="dashboard-empty-lead">Nous avons déjà référencé plus de {(statsCount ?? 21000).toLocaleString("fr-FR")} entreprises du Haut-Rhin. La vôtre est sûrement parmi elles. Cherchez son nom, réclamez-la, et vous êtes prêt.</p>
              <a href="/annuaire" className="inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-5 py-3 rounded-sm font-semibold text-sm transition-colors">
                🔍 Trouver mon entreprise
              </a>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              {n:1, t:"Cherchez", d:"Entrez le nom de votre entreprise dans l'annuaire"},
              {n:2, t:"Réclamez", d:"Cliquez sur « C'est mon entreprise » sur votre fiche"},
              {n:3, t:"Activez", d:"Passez partenaire pour apparaître en tête d'affiche"},
            ].map((s) => (
              <div key={s.n} className="bg-accent/30 border border-border rounded-sm p-5" data-testid={`dashboard-empty-step-${s.n}`}>
                <div className="w-8 h-8 bg-primary text-primary-foreground rounded-sm grid place-items-center font-display font-semibold mb-3">{s.n}</div>
                <div className="font-display text-lg font-medium mb-1">{s.t}</div>
                <div className="text-sm text-muted-foreground leading-relaxed">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <>
          {/* Selector */}
          {bizs.length > 1 && (
            <select value={active?.slug || ""} onChange={(e) => setActive(bizs.find((b) => b.slug === e.target.value))} className="border border-border bg-card rounded-sm px-3 py-2 text-sm mb-6" data-testid="dashboard-business-select">
              {bizs.map((b) => <option key={b.slug} value={b.slug}>{b.name}</option>)}
            </select>
          )}

          {active && (
            <>
              {/* Header card with quick actions */}
              <div className="bg-card border border-border rounded-sm p-6 mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="font-display text-2xl font-medium" data-testid="dashboard-active-name">{active.name}</h2>
                    {isPremium && <span className="inline-flex items-center gap-1 bg-primary text-primary-foreground text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-1 rounded-sm"><Crown size={10} weight="fill"/> {tier === "premium_ai" ? "Premium IA" : "Premium"}</span>}
                  </div>
                  <div className="text-sm text-muted-foreground">{active.category} · {active.city}</div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <a
                    href={`/entreprise/${active.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 border border-border bg-background hover:border-primary px-3 py-2 text-xs font-semibold rounded-sm transition-colors"
                    data-testid="view-public-link"
                  >
                    <Eye size={12} weight="bold"/> Voir ma fiche
                  </a>
                  {!isPremium && (
                    <a href="/passez-partenaire" className="bg-secondary text-secondary-foreground hover:bg-[#b56847] hover:text-white px-4 py-2 text-xs font-semibold rounded-sm transition-colors" data-testid="upgrade-cta">Passer Premium</a>
                  )}
                </div>
              </div>

              {/* Welcome hint — shown first time */}
              <WelcomeHint slug={active.slug} />

              {/* Vague 33A — Post-claim onboarding widget (auto-hides at 3/3) */}
              <PostClaimOnboarding slug={active.slug} onCompleted={reload} />

              {/* Fresh registration welcome — trial auto-offer (sticky CTA) */}
              <TrialWelcomeBanner biz={active} onActivated={reload} />

              {/* Free trial banner + countdown */}
              <TrialBanner biz={active} />

              {/* Financial impact — "why Premium is worth it" */}
              <ImpactPanel slug={active.slug} tier={tier} />

              {/* Partner badge — Premium only (free viral distribution) */}
              <BadgePanel slug={active.slug} tier={tier} />

              {/* Stats */}
              {analytics && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8" data-testid="dashboard-stats">
                  <Stat icon={<Eye size={18}/>} label="Vues" value={analytics.totals.views} />
                  <Stat icon={<Phone size={18}/>} label="Clics tél." value={analytics.totals.phone_clicks} />
                  <Stat icon={<WhatsappLogo size={18}/>} label="Clics WhatsApp" value={analytics.totals.whatsapp_clicks} />
                  <Stat icon={<Envelope size={18}/>} label="Demandes" value={analytics.totals.requests} />
                  <Stat icon={<ShareNetwork size={18}/>} label="Partages" value={analytics.totals.shares} />
                </div>
              )}

              {/* Flyer PDF + Monthly Report cards */}
              <div className="grid md:grid-cols-2 gap-4 mb-8">
                <div className="bg-card border border-border rounded-sm p-6 flex flex-col lift-card-plus" data-testid="flyer-card">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-12 h-12 shrink-0 bg-primary/10 text-primary grid place-items-center rounded-sm">
                      <FilePdf size={24} weight="duotone"/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-lg font-medium mb-1 flex items-center gap-2 flex-wrap">
                        Flyer PDF à imprimer
                        <span className="inline-flex items-center gap-1 bg-accent text-accent-foreground text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm"><QrCode size={10}/> QR</span>
                      </div>
                      <div className="text-sm text-muted-foreground leading-relaxed">
                        Flyer A5 avec QR code vers votre fiche. Idéal pour votre vitrine ou comptoir.
                      </div>
                      {flyerQuota && flyerQuota.allowed && (
                        <div className="text-xs text-muted-foreground mt-2" data-testid="flyer-quota-info">
                          Téléchargements ce mois-ci : <b className="text-foreground">{flyerQuota.remaining} / {flyerQuota.quota}</b>
                        </div>
                      )}
                      {flyerQuota && !flyerQuota.allowed && flyerQuota.tier !== "free" && (
                        <div className="text-xs text-secondary font-semibold mt-2" data-testid="flyer-quota-exhausted">
                          Quota atteint ({flyerQuota.used}/{flyerQuota.quota}) — renouvelé le 1er.
                        </div>
                      )}
                    </div>
                  </div>
                  {isPremium ? (
                    <button
                      onClick={downloadFlyer}
                      disabled={flyerBusy || (flyerQuota && !flyerQuota.allowed)}
                      className="mt-4 bg-primary text-primary-foreground hover:bg-[#0f3d29] px-4 py-2.5 rounded-sm font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all w-full"
                      data-testid="flyer-download-btn"
                    >
                      <FilePdf size={16} weight="fill"/> {flyerBusy ? "Génération…" : "Télécharger le flyer"}
                    </button>
                  ) : (
                    <a
                      href="/passez-partenaire"
                      className="mt-4 bg-secondary text-secondary-foreground hover:bg-[#b56847] hover:text-white px-4 py-2.5 rounded-sm font-semibold text-sm flex items-center justify-center gap-2 transition-colors w-full"
                      data-testid="flyer-upgrade-btn"
                    >
                      <Crown size={14} weight="fill"/> Passer Premium
                    </a>
                  )}
                </div>

                <div className="bg-card border border-border rounded-sm p-6 flex flex-col lift-card-plus" data-testid="report-card">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-12 h-12 shrink-0 bg-secondary/15 text-secondary grid place-items-center rounded-sm">
                      <ChartLine size={24} weight="duotone"/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-lg font-medium mb-1 flex items-center gap-2 flex-wrap">
                        Rapport mensuel PDF
                        <span className="inline-flex items-center gap-1 bg-accent text-accent-foreground text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm"><Sparkle size={10}/> IA</span>
                      </div>
                      <div className="text-sm text-muted-foreground leading-relaxed">
                        Vues, appels, WhatsApp, demandes, évolution vs mois précédent + conseils personnalisés IA.
                      </div>
                      <div className="text-xs text-muted-foreground mt-2">
                        Téléchargements <b className="text-foreground">illimités</b> · mis à jour chaque jour
                      </div>
                    </div>
                  </div>
                  {isPremium ? (
                    <button
                      onClick={downloadReport}
                      disabled={reportBusy}
                      className="mt-4 bg-secondary text-secondary-foreground hover:bg-[#b56847] hover:text-white px-4 py-2.5 rounded-sm font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all w-full"
                      data-testid="report-download-btn"
                    >
                      <ChartLine size={16} weight="bold"/> {reportBusy ? "Génération…" : "Télécharger le rapport"}
                    </button>
                  ) : (
                    <a
                      href="/passez-partenaire"
                      className="mt-4 bg-secondary text-secondary-foreground hover:bg-[#b56847] hover:text-white px-4 py-2.5 rounded-sm font-semibold text-sm flex items-center justify-center gap-2 transition-colors w-full"
                      data-testid="report-upgrade-btn"
                    >
                      <Crown size={14} weight="fill"/> Passer Premium
                    </a>
                  )}
                </div>
              </div>

              <div className="grid lg:grid-cols-3 gap-8">
                {/* Edit profile */}
                <div className="lg:col-span-2 bg-card border border-border rounded-sm p-6">
                  <h3 className="font-display text-xl font-medium mb-6">Modifier la fiche</h3>
                  {edit && (
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Field label="Nom" value={edit.name} onChange={(v) => setEdit({...edit, name: v})} testid="edit-name"/>
                      <Field label="Catégorie / Activité" value={edit.category} onChange={(v) => setEdit({...edit, category: v})} testid="edit-category"/>
                      <Field label="Téléphone" value={edit.phone} onChange={(v) => setEdit({...edit, phone: v})} testid="edit-phone" type="tel"/>
                      <Field label="WhatsApp" value={edit.whatsapp} onChange={(v) => setEdit({...edit, whatsapp: v})} testid="edit-whatsapp" type="tel" hint="Format international obligatoire : +33 6 12 34 56 78 (France) ou +22 pour autre pays. Sans le code, le bouton WhatsApp ne fonctionnera pas."/>
                      <Field label="Email" value={edit.email} onChange={(v) => setEdit({...edit, email: v})} testid="edit-email" type="email"/>
                      <Field label="Site web" value={edit.website} onChange={(v) => setEdit({...edit, website: v})} testid="edit-website" type="url"/>
                      <div className="sm:col-span-2">
                        <Field label="Adresse (rue + numéro)" value={edit.address} onChange={(v) => setEdit({...edit, address: v})} testid="edit-address"/>
                      </div>
                      <Field label="Code postal" value={edit.postal_code} onChange={(v) => setEdit({...edit, postal_code: v})} testid="edit-postal-code"/>
                      <Field label="Ville" value={edit.city} onChange={(v) => setEdit({...edit, city: v})} testid="edit-city"/>
                      <div className="sm:col-span-2">
                        <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1 block">Description (FR)</label>
                        <textarea value={edit.description} onChange={(e) => setEdit({...edit, description: e.target.value})} rows={4} className="w-full border border-border rounded-sm px-3 py-2 text-sm bg-background outline-none focus:border-primary" data-testid="edit-description"/>
                      </div>
                      <div className="sm:col-span-2">
                        <HoursEditor hours={edit.hours} onChange={(h) => setEdit({...edit, hours: h})} />
                      </div>
                      <button onClick={saveProfile} className="bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-5 py-2.5 rounded-sm font-semibold text-sm w-fit" data-testid="save-profile">Enregistrer</button>
                    </div>
                  )}

                  <div className="mt-8 pt-8 border-t border-border space-y-6">
                    <LogoUploader slug={active.slug} logo={active.logo} onChange={(url) => setActive({ ...active, logo: url })} />
                    <PhotoGallery slug={active.slug} photos={active.photos || []} onChange={(photos) => setActive({ ...active, photos })} tier={tier} />
                  </div>
                </div>

                {/* AI tools */}
                <AIToolsPanel business={active} isPremium={isPremium} />
              </div>

              {/* Reviews manager */}
              <ReviewsManager slug={active.slug} isPremium={isPremium} />

              {/* Widget d'avis embed (VAGUE 25) */}
              <ReviewsWidgetPanel business={active} />

              {/* Subscription manager */}
              <SubscriptionPanel slug={active.slug} isPremium={isPremium} />

              {/* Referral program */}
              <ReferralPanel slug={active.slug} businessName={active.name} />

              {/* Insights: Best Hours + Monopoly Local AI */}
              <InsightsPanel slug={active.slug} isPremium={isPremium} tier={tier} />

              {/* RGPD — droit à la portabilité (Article 20) */}
              <RgpdPanel />
            </>
          )}
        </>
      )}
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="bg-card border border-border rounded-sm p-5">
      <div className="flex items-center gap-2 text-muted-foreground text-xs font-bold uppercase tracking-wider"><span className="text-primary">{icon}</span> {label}</div>
      <div className="font-display text-3xl font-medium mt-2 tabular-nums">{value || 0}</div>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text", hint }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1 block">{label}</label>
      <input type={type} inputMode={type === "tel" ? "tel" : type === "email" ? "email" : type === "url" ? "url" : "text"} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full border border-border rounded-sm px-3 py-2 text-sm bg-background outline-none focus:border-primary" data-testid={testid}/>
      {hint && <div className="text-[11px] text-muted-foreground mt-1 leading-snug">{hint}</div>}
    </div>
  );
}




function TrialBanner({ biz }) {
  const premium = biz?.premium || {};
  const expiresIso = premium.expires_at;
  const isTrial = premium.trial === true && premium.tier !== "free";
  const [now, setNow] = React.useState(Date.now());
  React.useEffect(() => {
    if (!isTrial) return;
    const itv = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(itv);
  }, [isTrial]);
  if (!isTrial || !expiresIso) return null;
  const ms = new Date(expiresIso).getTime() - now;
  if (ms <= 0) return null;
  const days = Math.floor(ms / 86400000);
  const hours = Math.floor((ms % 86400000) / 3600000);
  const urgent = days <= 3;
  return (
    <div
      className={`rounded-sm p-5 mb-6 flex flex-col sm:flex-row items-start gap-4 border ${
        urgent ? "bg-secondary/10 border-secondary" : "bg-primary/5 border-primary/30"
      }`}
      data-testid="trial-banner"
    >
      <div className={`w-10 h-10 shrink-0 rounded-sm grid place-items-center ${urgent ? "bg-secondary text-secondary-foreground" : "bg-primary text-primary-foreground"}`}>
        <Crown size={18} weight="fill"/>
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display text-base font-medium mb-1">
          Essai Premium AI en cours — {days > 0 ? `${days}j ${hours}h` : `${hours}h`} restants
        </div>
        <div className="text-sm text-muted-foreground leading-relaxed">
          Vous profitez de toutes les fonctionnalités IA gratuitement pendant 14 jours. {urgent
            ? <b className="text-secondary">Votre essai se termine bientôt — passez Premium pour garder vos outils actifs.</b>
            : "Aucune carte n'a été demandée. À la fin de la période, votre fiche repassera en Free sauf si vous activez un abonnement."}
        </div>
      </div>
      <a
        href="/passez-partenaire"
        className={`shrink-0 px-4 py-2.5 text-sm font-semibold rounded-sm ${urgent ? "bg-secondary text-secondary-foreground hover:bg-[#b56847] hover:text-white" : "bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white"}`}
        data-testid="trial-upgrade-cta"
      >
        Passer Premium
      </a>
    </div>
  );
}


function WelcomeHint({ slug }) {
  const KEY = `mc68_dashboard_welcome_${slug}`;
  const [show, setShow] = React.useState(false);
  React.useEffect(() => {
    setShow(!localStorage.getItem(KEY));
  }, [slug]);
  if (!show) return null;
  const dismiss = () => { localStorage.setItem(KEY, "1"); setShow(false); };
  return (
    <div className="bg-accent/30 border border-border rounded-sm p-5 mb-6 flex flex-col sm:flex-row items-start gap-4" data-testid="welcome-hint">
      <div className="w-10 h-10 shrink-0 rounded-sm bg-primary text-primary-foreground grid place-items-center">
        <Sparkle size={18} weight="fill"/>
      </div>
      <div className="flex-1">
        <div className="font-display text-base font-medium mb-1">Bienvenue dans votre espace</div>
        <div className="text-sm text-muted-foreground leading-relaxed">
          Complétez votre fiche (description, photos, WhatsApp), puis explorez vos statistiques et l'outil <b className="text-foreground">Suggestion IA</b> des avis. Tout est pensé pour prendre 5 minutes par semaine.
        </div>
      </div>
      <button onClick={dismiss} className="text-xs text-muted-foreground hover:text-foreground font-semibold" data-testid="welcome-hint-dismiss" aria-label="Masquer">
        Masquer
      </button>
    </div>
  );
}


